import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import { api, apiBlob } from "../api";
import { PageHeader } from "../components/PageHeader";
import { StatusChip } from "../components/StatusChip";

export function ScanDetailPage() {
  const { scanId = "" } = useParams();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<{
    name: string;
    mime: string;
    url: string;
  } | null>(null);
  const load = () =>
    api<any>(`/admin/scans/${scanId}`)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [scanId]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );

  const openBlob = async (
    path: string,
    name: string,
    mime: string,
    download = false,
  ) => {
    try {
      const url = URL.createObjectURL(await apiBlob(path));
      if (download) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = name;
        anchor.click();
        URL.revokeObjectURL(url);
      } else setPreview({ name, mime, url });
    } catch (e) {
      setError(e instanceof Error ? e.message : "File request failed");
    }
  };

  if (error)
    return (
      <Alert severity="error" onClose={() => setError("")}>
        {error}
      </Alert>
    );
  if (!data) return <CircularProgress />;
  const s = data.scan;
  return (
    <>
      <PageHeader
        title="Scan detail"
        subtitle={s.id}
        actions={
          <>
            {s.status === "FAILED" && (
              <Button
                variant="contained"
                startIcon={<ReplayIcon />}
                onClick={async () => {
                  await api(`/admin/scans/${s.id}/retry`, { method: "POST" });
                  void load();
                }}
              >
                Retry failed files
              </Button>
            )}
            <StatusChip value={s.status} />
          </>
        }
      />
      <div className="metric-grid">
        <Metric label="Client" value={data.client?.name ?? s.client_id} />
        <Metric
          label="Images"
          value={`${s.processed_count} / ${s.image_count} processed`}
        />
        <Metric
          label="Created"
          value={new Date(s.created_at).toLocaleString()}
        />
      </div>
      {s.error_message && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {s.error_message}
        </Alert>
      )}
      <Card sx={{ mt: 2.5 }}>
        <Box p={2.5}>
          <Typography variant="h6">Files</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Attempts</TableCell>
              <TableCell>Azure resource</TableCell>
              <TableCell>Error</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.images.map((img: any) => (
              <TableRow key={img.id}>
                <TableCell>{img.sequence_no}</TableCell>
                <TableCell>{img.file_name}</TableCell>
                <TableCell>
                  <StatusChip value={img.status} />
                </TableCell>
                <TableCell>{formatBytes(img.file_size)}</TableCell>
                <TableCell>{img.attempt_count}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {img.azure_account_name ?? "Legacy provider"}
                  </Typography>
                  {img.azure_account_category && (
                    <Typography variant="caption" color="text.secondary">
                      {img.azure_account_category} ·{" "}
                      {img.azure_failover_count ?? 0} failovers
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography
                    variant="caption"
                    color="error"
                    className="truncate"
                    display="block"
                  >
                    {img.error_message}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="Preview input">
                    <IconButton
                      onClick={() =>
                        void openBlob(
                          `/admin/scans/${s.id}/images/${img.id}/input`,
                          img.file_name,
                          img.mime_type,
                        )
                      }
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {img.ocr_r2_key && (
                    <Tooltip title="Download Azure JSON">
                      <IconButton
                        onClick={() =>
                          void openBlob(
                            `/admin/scans/${s.id}/images/${img.id}/ocr`,
                            `${img.id}.json`,
                            "application/json",
                            true,
                          )
                        }
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {data.invoice && (
        <Card sx={{ mt: 2.5 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">Normalized invoice</Typography>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() =>
                  void openBlob(
                    `/admin/scans/${s.id}/result`,
                    `${s.id}.json`,
                    "application/json",
                    true,
                  )
                }
              >
                Download JSON
              </Button>
            </Stack>
            <Box
              component="pre"
              sx={{
                fontSize: 12,
                bgcolor: "#f6f8f9",
                p: 2,
                borderRadius: 2,
                overflow: "auto",
                maxHeight: 500,
              }}
            >
              {JSON.stringify(data.invoice, null, 2)}
            </Box>
          </CardContent>
        </Card>
      )}
      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{preview?.name}</DialogTitle>
        <DialogContent>
          {preview?.mime === "application/pdf" ? (
            <Box
              component="iframe"
              src={preview?.url}
              sx={{ width: "100%", height: "70vh", border: 0 }}
            />
          ) : (
            <Box
              component="img"
              src={preview?.url}
              alt={preview?.name}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "70vh",
                mx: "auto",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent>
        <Typography color="text.secondary" variant="body2">
          {label}
        </Typography>
        <Typography variant="h6">{value}</Typography>
      </CardContent>
    </Card>
  );
}
const formatBytes = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1048576
      ? `${(n / 1024).toFixed(1)} KB`
      : `${(n / 1048576).toFixed(1)} MB`;
